import { useTranslation } from "react-i18next";
import { CreateAnecdoteRequest } from "../types/api/anecdotes";

type AnecdoteFriendFormProps = {
  index: number;
  value: CreateAnecdoteRequest;
  defaultMessage: string;
  onChange: (index: number, field: keyof CreateAnecdoteRequest, value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
};

export const AnecdoteFriendForm = ({
  index,
  value,
  defaultMessage,
  onChange,
  onRemove,
  canRemove,
}: AnecdoteFriendFormProps) => {
  const { t } = useTranslation();

  return (
    <div className="border border-gray-200 rounded-lg p-5 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-sm font-semibold text-gray-900">
          {t("anecdotes.modal.form.friend", { index: index + 1 })}
        </h4>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
            title={t("anecdotes.modal.form.removeTitle")}
          >
            {t("anecdotes.modal.form.remove")}
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Prénom and Nom in a row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor={`friend_first_name_${index}`}
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              {t("anecdotes.modal.form.firstName")}
            </label>
            <input
              type="text"
              id={`friend_first_name_${index}`}
              value={value.friend_first_name}
              onChange={(e) =>
                onChange(index, "friend_first_name", e.target.value)
              }
              placeholder=""
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor={`friend_last_name_${index}`}
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              {t("anecdotes.modal.form.lastName")}
            </label>
            <input
              type="text"
              id={`friend_last_name_${index}`}
              value={value.friend_last_name}
              onChange={(e) => onChange(index, "friend_last_name", e.target.value)}
              placeholder=""
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor={`friend_email_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            {t("anecdotes.modal.form.email")}
          </label>
          <input
            type="email"
            id={`friend_email_${index}`}
            value={value.friend_email}
            onChange={(e) => onChange(index, "friend_email", e.target.value)}
            placeholder=""
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Message personnalisé */}
        <div>
          <label
            htmlFor={`custom_message_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            {t("anecdotes.modal.form.customMessage")}
          </label>
          <textarea
            id={`custom_message_${index}`}
            value={value.custom_message || defaultMessage}
            onChange={(e) => onChange(index, "custom_message", e.target.value)}
            rows={5}
            placeholder={defaultMessage}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
          />
        </div>
      </div>
    </div>
  );
};
